import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { resolveSrc } from '../../../api/endpoints';
import purifyHTML from '../../../utils/purify-html';
import CardWeather from '../../files/Widgets/CardWeather';
import html2canvas from 'html2canvas';
import { buildLiveEmbedUrl } from '../../../utils/liveEmbed';
// TV frame with orientation-aware fixed resolution (portrait 1080x1920 or landscape 1920x1080)
const TvFrame = ({ children, orientation = 'landscape' }) => {
  const isPortrait = orientation === 'portrait';
  const baseWidth = isPortrait ? 540 : 960;
  const baseHeight = isPortrait ? 850 : 540;
  // Inner screen rectangle (kept with some margins similar to original design)
  const screenBox = isPortrait
    ? { x: 0, y: 0, w: 540, h: 850 }
    : { x: 0, y: 0, w: 960, h: 540 };

  return (
    <Box
      sx={{
        width: '80%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, md: 3 },
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: baseWidth,
          height: baseHeight,
          flexShrink: 0,
        //   border: '4px solid #151515',
          backgroundColor: '#000',
        }}
      >
        {/* <Box
          component="svg"
          sx={{ width: baseWidth, height: baseHeight, position: 'absolute', inset: 0 }}
          viewBox={`0 0 ${baseWidth} ${baseHeight}`}
          preserveAspectRatio="xMidYMid meet"
          aria-label="TV frame decorative"
        >
          <defs>
            <linearGradient id="bezelGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a3a3a" />
              <stop offset="50%" stopColor="#111" />
              <stop offset="100%" stopColor="#1c1c1c" />
            </linearGradient>
            <filter id="innerShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feOffset dx="0" dy="0" />
              <feGaussianBlur stdDeviation="18" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          <rect
            x={10}
            y={10}
            width={baseWidth - 20}
            height={baseHeight - 20}
            rx={30}
            fill="url(#bezelGrad)"
            stroke="#000"
            strokeWidth={20}
          />
          <rect
            x={screenBox.x}
            y={screenBox.y}
            width={screenBox.w}
            height={screenBox.h}
            rx={24}
            fill="#000"
            filter="url(#innerShadow)"
          />
          {!isPortrait && (
            <>
              <circle cx={baseWidth - 50} cy={baseHeight - 50} r={10} fill="#e00" />
              <rect x={baseWidth / 2 - 160} y={baseHeight - 20} width={320} height={20} fill="#222" />
              <rect x={baseWidth / 2 - 200} y={baseHeight + 0} width={400} height={25} rx={8} fill="#151515" />
            </>
          )}
        </Box> */}
        <Box
          sx={{
            position: 'absolute',
            left: screenBox.x,
            top: screenBox.y,
            width: screenBox.w,
            height: screenBox.h,
            overflow: 'hidden',
            borderRadius: '5px',
            // boxShadow: 'inset 0 0 12px rgba(255,255,255,0.05)',
          }}
        >
          <Box sx={{ width: '100%', height: '100%' }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
};

const DEFAULT_DURATION = 10000;
const MINIMUM_DURATION = 1000;

const hostname = (() => {
  const fallback =
    typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const environmental = process.env.REACT_APP_BASE_URL;
  if (!environmental) {
    return fallback;
  }
  try {
    return new URL(environmental).hostname;
  } catch (error) {
    console.warn('Unable to parse REACT_APP_BASE_URL', error);
    return fallback;
  }
})();

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const EMBED_IMAGE_URL_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

const isDirectEmbedImageUrl = (url = '') => EMBED_IMAGE_URL_REGEX.test(url);

const extractEmbeddedWidgetImageSrc = (rawHtml = '') => {
  const html = rawHtml.trim();
  if (!html) return null;

  const imageTagMatch = html.match(
    /^<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*\/?>(?:\s*)$/i
  );
  if (imageTagMatch?.[1]) {
    return imageTagMatch[1].replaceAll('&amp;', '&');
  }

  const iframeTagMatch = html.match(
    /^<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*><\/iframe>(?:\s*)$/i
  );
  const iframeSrc = iframeTagMatch?.[1]?.replaceAll('&amp;', '&');
  if (iframeSrc && EMBED_IMAGE_URL_REGEX.test(iframeSrc)) {
    return iframeSrc;
  }

  return null;
};

const extractIframeSrc = (rawHtml = '') => {
  const html = rawHtml.trim();
  if (!html) return '';
  const iframeTagMatch = html.match(/^<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*>(?:<\/iframe>)?(?:\s*)$/i);
  return iframeTagMatch?.[1]?.replaceAll('&amp;', '&') || '';
};

const normalizeEmbedHtml = (rawHtml = '') => {
  const html = rawHtml || '';
  return html.replace(/<iframe\b[^>]*>/gi, (iframeTag) => {
    let normalizedTag = iframeTag
      .replace(/\swidth=["'][^"']*["']/gi, '')
      .replace(/\sheight=["'][^"']*["']/gi, '');

    if (/\sscrolling=/i.test(normalizedTag)) {
      normalizedTag = normalizedTag.replace(
        /\sscrolling=["'][^"']*["']/gi,
        ' scrolling="no"'
      );
    } else {
      normalizedTag = normalizedTag.replace(/>$/, ' scrolling="no">');
    }

    const forcedFrameStyle =
      'width:100% !important;height:100% !important;max-width:100% !important;max-height:100% !important;overflow:hidden;';

    if (/\sstyle=["'][^"']*["']/i.test(normalizedTag)) {
      normalizedTag = normalizedTag.replace(
        /\sstyle=["']([^"']*)["']/i,
        (_match, styleValue) => ` style="${styleValue};${forcedFrameStyle}"`
      );
    } else {
      normalizedTag = normalizedTag.replace(
        />$/,
        ` style="${forcedFrameStyle}">`
      );
    }

    return normalizedTag;
  });
};

const normaliseDuration = (rawDuration) => {
  const numeric = Number(rawDuration);
  if (!Number.isFinite(numeric) || numeric <= 0) return DEFAULT_DURATION;
  if (numeric < MINIMUM_DURATION) return numeric * 1000;
  return numeric;
};

function detectUrlType(u) {
    if (!u || typeof u !== 'string') return 'unknown';
    const lower = u.split('?')[0].split('#')[0].toLowerCase();

    if (lower.match(/\.(mp4|webm|mov|m4v)$/)) return 'video';
    if (lower.match(/\.(jpe?g|png|gif|webp)$/)) return 'image';
    return 'unknown';
}

const collectPlaylistItems = (playlist) => {
  if (!playlist) return [];

  const acc = [];

  const pushEntry = (entry) => {
    if (!entry) return;
    const content = entry.content ?? entry;
    if (!content) return;

    const widget = content.widget ?? entry.widget ?? null;
    const url = widget?.url ?? content.url ?? entry.url ?? '';
    const lowerType = widget?.widgetCat == 'postermywall' ? detectUrlType(url) : (
      content.type ??
      entry.type ??
      widget?.widgetCat ??
      ''
    ).toLowerCase();
    const html = widget?.html ?? content.html ?? entry.html ?? '';

    acc.push({
      id: content._id ?? entry._id ?? `${lowerType}-${acc.length}`,
      name: content.name ?? entry.name ?? content.title ?? '',
      type: lowerType,
      widget,
      url,
      html,
      duration: normaliseDuration(entry.duration ?? content.duration),
      raw: entry,
    });
  };

  const parseNode = (node) => {
    if (!node) return;
    if (Array.isArray(node.playlist)) {
      node.playlist.forEach((inner) => {
        ensureArray(inner?.content).forEach(pushEntry);
      });
      return;
    }

    const candidateContent =
      node.content ?? node.playlist?.content ?? node?.playlist?.[0]?.content;
    if (candidateContent) {
      ensureArray(candidateContent).forEach(pushEntry);
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(parseNode);
      return;
    }
    pushEntry(node);
  };

  if (Array.isArray(playlist)) {
    playlist.forEach(parseNode);
  } else if (playlist?.playlist && Array.isArray(playlist.playlist)) {
    playlist.playlist.forEach(parseNode);
  } else if (playlist?.content) {
    ensureArray(playlist.content).forEach(pushEntry);
  } else {
    parseNode(playlist);
  }

  return acc;
};

const buildYouTubeSrc = (url, isPlaylist) => {
  if (!url) return '';
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    const list = parsed.searchParams.get('list');
    const vId =
      parsed.searchParams.get('v') ||
      parsed.pathname.split('/').filter(Boolean).pop();

    if (isPlaylist || list) {
      const playlistId = list || vId;
      if (!playlistId) return url;
      return `https://www.youtube.com/embed?listType=playlist&list=${playlistId}&autoplay=1&mute=1&controls=0&modestbranding=1`;
    }

    if (!vId) return url;
    return `https://www.youtube.com/embed/${vId}?autoplay=1&mute=1&controls=0&modestbranding=1`;
  } catch (error) {
    console.warn('Failed to build YouTube src', error);
    return url;
  }
};

// Extract a YouTube video id from a URL for thumbnail rendering
const extractYouTubeVideoId = (url) => {
  if (!url) return '';
  try {
    const u = url.startsWith('http') ? url : `https://${url}`;
    const parsed = new URL(u);
    // Standard watch URL ?v=
    let id = parsed.searchParams.get('v');
    if (id) return id;
    // youtu.be short links
    if (parsed.hostname.includes('youtu.be')) {
      const seg = parsed.pathname.split('/').filter(Boolean).shift();
      if (seg) return seg;
    }
    // /embed/<id> or /v/<id> or general last path segment
    const pathSegs = parsed.pathname.split('/').filter(Boolean);
    const embedIdx = pathSegs.findIndex((s) => s === 'embed' || s === 'v');
    if (embedIdx !== -1 && pathSegs[embedIdx + 1])
      return pathSegs[embedIdx + 1];
    const last = pathSegs.pop();
    return last || '';
  } catch {
    return '';
  }
};

const buildTwitchSources = (url, widget) => {
  if (!url) return { playerSrc: '', chatSrc: '', isClip: false };
  if (!hostname) return { playerSrc: url, chatSrc: '', isClip: false };
  const hasClip = url.includes('/clip/');
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    const segments = parsed.pathname.split('/').filter(Boolean);
    let playerSrc = url;
    let chatSrc = '';
    let channelName = segments[0] || '';

    if (url.includes('/videos/')) {
      const videoId = segments.pop();
      channelName = segments[0] || channelName;
      playerSrc = `https://player.twitch.tv/?video=${videoId}&parent=${hostname}&autoplay=true`;
    } else if (hasClip) {
      const clipId = segments.pop();
      playerSrc = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${hostname}&autoplay=true`;
    } else {
      playerSrc = `https://player.twitch.tv/?channel=${channelName}&parent=${hostname}&autoplay=true${
        widget?.chat ? '&muted=true' : ''
      }`;
    }

    if (widget?.chat && !hasClip) {
      chatSrc = `https://www.twitch.tv/embed/${channelName}/chat?parent=${hostname}${
        widget?.theme === 'Dark Theme' ? '&darkpopout=true' : ''
      }`;
    }

    return { playerSrc, chatSrc, isClip: hasClip };
  } catch (error) {
    console.warn('Failed to build Twitch src', error);
    return { playerSrc: url, chatSrc: '', isClip: false };
  }
};

const renderWeatherWidget = (weather) => {
  if (!weather) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        <Typography variant='body2'>No weather configuration</Typography>
      </Box>
    );
  }

  const tpl = (weather.template || '').trim();
  const forecastTpl = (weather.forecastItemTemplate || '').trim();
  const theme = weather.themeSettings || {};

  // Basic safe replacements helper
  const replaceAll = (str, map) => {
    let out = str;
    Object.entries(map).forEach(([key, val]) => {
      const safeVal = val == null ? '' : String(val);
      out = out.replaceAll(`{{${key}}}`, safeVal);
    });
    return out;
  };

  // Build forecast HTML if array provided
  let forecastHtml = '';
  const forecast = weather.forecast;
  if (Array.isArray(forecast) && forecastTpl) {
    forecastHtml = forecast
      .map((f) =>
        replaceAll(forecastTpl, {
          background: f.background,
          datetimeMobile: f.datetimeMobile,
          datetimeDesktop: f.datetimeDesktop,
          weatherIcon: f.weatherIcon,
          maxTemperature: f.maxTemperature,
          minTemperature: f.minTemperature,
        })
      )
      .join('\n');
  }

  // Build main template
  let html = replaceAll(tpl, {
    bgImg: theme.bgImg,
    bgColor: theme.bgColor,
    fontSize:
      weather.fontSize === 'S' ? 16 : weather.fontSize === 'L' ? 22 : 18,
    formattedAddress: weather.location?.formattedAddress,
    weatherIcon: weather.weatherIcon,
    temperature: weather.temperature,
    scale: weather.scale || 'C',
    humidity: weather.humidity,
    pressure: weather.pressure,
    currentTime: weather.currentTime,
    forecast: forecastHtml,
  });

  // Strictly preserve src URLs from data (including /assets paths)
  const safeHtml = purifyHTML.sanitize(html, {
    ADD_TAGS: ['style'],
    ADD_ATTR: ['style', 'class'],
  });
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        // Ensure container queries work as intended
        containerType: 'inline-size',
        containerName: 'weather-widget',
        display: 'block',
      }}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

// Playlist player for split-screen zones; cycles through items with their durations
function ZonePlaylistPlayer({ playlistItems, cachedWeatherImages = {} }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [playlistItems]);

  useEffect(() => {
    if (!playlistItems || playlistItems.length <= 1) return undefined;
    const duration = playlistItems[index]?.duration || DEFAULT_DURATION;
    const timeout = setTimeout(
      () => setIndex((prev) => (prev + 1) % playlistItems.length),
      duration
    );
    return () => clearTimeout(timeout);
  }, [playlistItems, index]);

  if (!playlistItems || playlistItems.length === 0) return null;
  return renderContent(playlistItems[index], cachedWeatherImages);
}

const renderContent = (item, cachedWeatherImages = {}) => {
  if (!item) return null;
  const { type, widget, url, html } = item;
  const effectiveType =
    type === 'widget' || !type ? widget?.widgetCat?.toLowerCase() ?? '' : type;

  if (['weather'].includes(effectiveType)) {
    // Check if we have a cached image for this weather widget
    const cachedImage = cachedWeatherImages[item.id];
    if (cachedImage) {
      return (
        <Box
          component='img'
          src={cachedImage}
          alt='Weather widget'
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      );
    }
    // Fallback to live CardWeather if cache not available
    return (
      <CardWeather
        item={{ weather: widget?.weather, theme: widget?.theme }}
        style={{ width: '100%', height: '100%', overflow: 'hidden' }}
        cssOverflow='auto'
        cssJustifyContent={widget?.weather?.cssJustifyContent || 'flex-start'}
        cssFontSize={widget?.weather?.fontSize}
        compact
      />
    );
  }

  switch (effectiveType) {
    case 'image':
      return (
        <Box
          component='img'
          src={resolveSrc(url)}
          alt={item.name ?? ''}
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      );
    case 'video':
      return (
        <Box
          component='video'
          src={resolveSrc(url)}
          autoPlay
          loop
          muted
          controls
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: 'black',
          }}
        />
      );
    case 'youtube': {
      const videoId = extractYouTubeVideoId(url);
      if (!videoId) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            <Typography variant='body2'>Invalid YouTube URL</Typography>
          </Box>
        );
      }
      const thumb = `http://img.youtube.com/vi/${videoId}/0.jpg`;
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundColor: 'black',
          }}
        >
          <Box
            component='img'
            src={thumb}
            alt={item.name || 'YouTube thumbnail'}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
          {/* YouTube icon overlay */}
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: '999px',
              bgcolor: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(2px)',
            }}
            aria-label='YouTube'
          >
            <Box component='span' sx={{ display: 'inline-flex' }}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M23.5 6.2c-.2-1.4-1.4-2.5-2.8-2.7C18.4 3.1 12 3.1 12 3.1s-6.4 0-8.7.4C1.9 3.7.7 4.8.5 6.2.1 8.5 0 12 0 12s.1 3.5.5 5.8c.2 1.4 1.4 2.5 2.8 2.7 2.3.4 8.7.4 8.7.4s6.4 0 8.7-.4c1.4-.2 2.6-1.3 2.8-2.7.4-2.3.5-5.8.5-5.8s-.1-3.5-.5-5.8z'
                  fill='#FF0000'
                />
                <path d='M9.6 15.3V8.7l6.2 3.3-6.2 3.3z' fill='#fff' />
              </svg>
            </Box>
            <Typography variant='caption' sx={{ color: '#fff', lineHeight: 1 }}>
              YouTube
            </Typography>
          </Box>
        </Box>
      );
    }
    case 'youtube playlist':
    case 'yt_playlist': {
      const playlistSrc = buildYouTubeSrc(url, true);
      if (!playlistSrc) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            <Typography variant='body2'>
              Invalid YouTube Playlist URL
            </Typography>
          </Box>
        );
      }
      return (
        <iframe
          src={playlistSrc}
          title={item.name || 'YouTube Playlist'}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      );
    }
    case 'vimeo':
      return (
        <iframe
          src={url}
          title={item.name || 'Vimeo'}
          allow='autoplay; fullscreen'
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      );
    case 'live_youtube':
    case 'live_twitch':
    case 'live_vimeo': {
      const liveSrc = buildLiveEmbedUrl(widget);
      return liveSrc ? (
        <iframe
          src={liveSrc}
          title={item.name || 'Live stream'}
          allow='autoplay; fullscreen; encrypted-media'
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          <Typography variant='body2'>Live stream unavailable</Typography>
        </Box>
      );
    }
    case 'twitch': {
      const { playerSrc, chatSrc } = buildTwitchSources(url, widget);
      return (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <iframe
            title={item.name || 'Twitch'}
            src={playerSrc}
            allowFullScreen
            frameBorder={0}
            style={{
              width: chatSrc ? '70%' : '100%',
              height: '100%',
              border: 'none',
            }}
          />
          {chatSrc && (
            <iframe
              title='Twitch Chat'
              src={chatSrc}
              frameBorder={0}
              style={{ width: '30%', height: '100%', border: 'none' }}
            />
          )}
        </Box>
      );
    }
    case 'scrollingstrip': {
      // If DB HTML exists, render it directly (settings like speed, colors, fonts preserved)
      if (html) {
        // Allow style tags & class/style attributes so stored animation & formatting work
        const safeHtml = purifyHTML.sanitize(html, {
          ADD_TAGS: ['style'],
          ADD_ATTR: ['style', 'class'],
        });
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{ width: '100%', height: '100%' }}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </Box>
        );
      }
      // Fallback to legacy generated strip when HTML missing
      const rawContent = (url || '')
        .replaceAll('>\\n', '<br/>')
        .replaceAll('\\', '')
        .trim();
      const safeContent = purifyHTML.sanitize(rawContent, {
        ALLOWED_TAGS: ['b', 'i', 'strong', 'em', 'span', 'br'],
        ALLOWED_ATTR: ['style'],
      });
      const tempEl = document.createElement('div');
      tempEl.innerHTML = safeContent;
      const textLength = tempEl.textContent.length || 20;
      const durationSec = Math.min(Math.max(textLength * 0.35, 15), 90);
      const animationName = `scrollStrip_${Math.random()
        .toString(36)
        .slice(2)}`;
      const keyframes = `@keyframes ${animationName} { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }`;
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              width: { xs: '90%', md: '70%' },
              maxWidth: '1400px',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.25)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <style>{keyframes}</style>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                sx={{
                  display: 'inline-block',
                  px: 4,
                  animation: `${animationName} ${durationSec}s linear infinite`,
                  fontSize: { xs: '0.95rem', md: '1.15rem' },
                  fontWeight: 500,
                }}
                dangerouslySetInnerHTML={{ __html: safeContent }}
              />
            </Box>
          </Box>
        </Box>
      );
    }
    case 'website':
    case 'postermywall':
    case 'canva':
      const formatCanvaUrl = (raw) => {
        if (!raw) return '';
        let u = raw.trim();
        // Ensure protocol
        if (!/^https?:\/\//i.test(u)) {
          u = `https://${u}`;
        }
        // Remove leading slash before protocol (e.g. /https://...)
        u = u.replace(/^\/+(https?:\/\/)/i, '$1');
        // If the string contains an internal second https:// (e.g. http://localhost:3000/https://www.canva...)
        // strip everything before the last proper https:// occurrence that points to canva.com
        const lastHttpsIdx = u.lastIndexOf('https://');
        if (lastHttpsIdx > 0) {
          // Check if substring after last occurrence references canva.com
          const tail = u.slice(lastHttpsIdx);
          if (/https:\/\/[^/]*canva\.com/i.test(tail)) {
            u = tail; // keep only valid external portion
          }
        }
        try {
          const parsed = new URL(u);
          if (
            parsed.hostname.includes('canva.com') &&
            parsed.pathname.includes('/design/')
          ) {
            const parts = parsed.pathname.split('/').filter(Boolean); // [design, DESIGN_ID, ...]
            const designIndex = parts.indexOf('design');
            const designId = designIndex !== -1 ? parts[designIndex + 1] : null;
            if (designId) {
              return `https://www.canva.com/design/${designId}/view?embed`; // Canonical embed form
            }
          }
          // If we accidentally still have a localhost reference, fall back to original parsed href only if it's canva.com
          if (!/canva\.com/i.test(parsed.hostname)) {
            // attempt to extract canva url from the entire raw string
            const match = raw.match(/https?:\/\/[^\s"]*canva\.com[^\s"]*/i);
            if (match) return match[0];
          }
          return parsed.toString();
        } catch (e) {
          return u;
        }
      };
      const altMediaUrl = (widget?.altMediaUrl || '').trim();
    //   console.log(altMediaUrl)
      if (altMediaUrl) {
        return (
          <Box
            component='img'
            src={resolveSrc(altMediaUrl)}
            alt={item.name || 'Canva media'}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        );
      }
      if (html) {
        // Clean the HTML: unescape and remove localhost prefixes from iframe src attributes
        let cleanedHtml = html.replaceAll('>\\n', '<br/>').replaceAll('\\', '');
        cleanedHtml = cleanedHtml.replace(
          /src=["']https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//gi,
          'src="'
        );
        return (
          <Box
            sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
            dangerouslySetInnerHTML={{ __html: cleanedHtml }}
          />
        );
      }
      const canvaSrc = formatCanvaUrl(url);
      return (
        <iframe
          src={canvaSrc}
          title={item.name || 'Canva'}
          style={{ width: '100%', height: '100%', border: 'none' }}
          allow='clipboard-write; fullscreen'
        />
      );
    case 'embed':
      if (isDirectEmbedImageUrl(url)) {
        return (
          <Box
            component='img'
            src={resolveSrc(url)}
            alt={item.name ?? ''}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        );
      }

      if (widget?.widgetCat === 'embed') {
        const embeddedWidgetImageSrc = extractEmbeddedWidgetImageSrc(html);
        if (embeddedWidgetImageSrc) {
          return (
            <Box
              component='img'
              src={resolveSrc(embeddedWidgetImageSrc)}
              alt={item.name ?? ''}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          );
        }

        const iframeSrc = extractIframeSrc(html);
        if (iframeSrc) {
          return (
            <iframe
              src={iframeSrc}
              title={item.name || 'Embed'}
              scrolling='no'
              referrerPolicy='no-referrer-when-downgrade'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          );
        }

        const normalizedHtml = normalizeEmbedHtml(html);
        const safeHtml = purifyHTML.sanitize(normalizedHtml, {
          ADD_TAGS: ['iframe', 'embed', 'object', 'style'],
          ADD_ATTR: [
            'style',
            'class',
            'src',
            'width',
            'height',
            'scrolling',
            'frameborder',
            'allow',
            'allowfullscreen',
          ],
        });

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              '& iframe, & embed, & object': {
                width: '100% !important',
                height: '100% !important',
                maxWidth: '100% !important',
                maxHeight: '100% !important',
                border: 0,
                display: 'block',
              },
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        );
      }

      if (!html && url) {
        return (
          <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <embed
              src={resolveSrc(url)}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </Box>
        );
      }

      return (
        <Box
          sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
          dangerouslySetInnerHTML={{ __html: html || '' }}
        />
      );
    case 'playlist': {
      const playlistSource =
        item.playlist ||
        item.raw?.playlist ||
        item.raw?.content ||
        item.content;
      const playlistItems = collectPlaylistItems(playlistSource);
      if (!playlistItems.length) return null;
      if (playlistItems.length === 1) {
        return renderContent(playlistItems[0], cachedWeatherImages);
      }
      return (
        <ZonePlaylistPlayer
          playlistItems={playlistItems}
          cachedWeatherImages={cachedWeatherImages}
        />
      );
    }
    case 'split_screen':
    case 'split': {
      const zones = widget?.zones || [];
      if (!zones.length) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            <Typography variant='body2'>No zones configured</Typography>
          </Box>
        );
      }
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {zones.map((zone, idx) => {
            const xPercent = (zone.x || 0) * 100;
            const yPercent = (zone.y || 0) * 100;
            const widthPercent = (zone.width || 0) * 100;
            const heightPercent = (zone.height || 0) * 100;

            return (
              <Box
                key={zone.id || `zone-${idx}`}
                sx={{
                  position: 'absolute',
                  left: `${xPercent}%`,
                  top: `${yPercent}%`,
                  width: `${widthPercent}%`,
                  height: `${heightPercent}%`,
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxSizing: 'border-box',
                }}
              >
                {renderZoneContent(zone, cachedWeatherImages)}
              </Box>
            );
          })}
        </Box>
      );
    }
    default:
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
            px: 4,
          }}
        >
          <Typography variant='body1'>
            Unsupported media type: {effectiveType || 'unknown'}
          </Typography>
        </Box>
      );
  }
};

const renderZoneContent = (zone, cachedWeatherImages = {}) => {
  if (!zone) return null;

  // Some zones store content directly, others wrap it in .object
  const obj = zone.object || zone;
  const objType = obj.type?.toLowerCase() || '';
  const objWidget = obj.widget;

  // Support playlists nested inside split screen zones (type or property)
  if (objType === 'playlist' || obj?.playlist || obj?.content) {
    const playlistSource = obj.playlist || obj.content || obj.raw?.playlist;
    const playlistItems = collectPlaylistItems(playlistSource);
    if (playlistItems.length > 0) {
      return (
        <ZonePlaylistPlayer
          playlistItems={playlistItems}
          cachedWeatherImages={cachedWeatherImages}
        />
      );
    }
  }

  // Extract URL from multiple possible locations
  let extractedUrl = obj.url || zone.content_path || '';

  // For widgets (like YouTube), check widget.url as well
  if (objType === 'widget' && objWidget) {
    extractedUrl = objWidget.url || objWidget.content_path || extractedUrl;
  }

  // Create a temporary item structure for rendering
  const tempItem = {
    id: obj.id || zone.id,
    name: obj.name || zone.content_name || zone.name,
    type: objType,
    widget: objWidget,
    url: extractedUrl,
    html: objWidget?.html || obj.html || '',
    duration: obj.duration || DEFAULT_DURATION,
    raw: obj,
  };

  return renderContent(tempItem, cachedWeatherImages);
};

const RedesignPlayer = ({ playlist, open, onClose, portable, hideNav = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const mediaItems = useMemo(() => collectPlaylistItems(playlist), [playlist]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [orientation, setOrientation] = useState('portrait');
  const [autoSwitchedOrientation, setAutoSwitchedOrientation] = useState(false);
  const [cachedWeatherImages, setCachedWeatherImages] = useState({});
  const [isCachingWeather, setIsCachingWeather] = useState(false);
  const hiddenCacheRef = useRef(null);

  useEffect(() => {
    setCurrentIndex(0);
  }, [open, mediaItems.length]);

  // Reset auto-switch flag on open
  useEffect(() => {
    if (open) {
      setAutoSwitchedOrientation(false);
      // Ensure we start in portrait each time dialog opens
      setOrientation('portrait');
    }
  }, [open]);

  // Auto-switch portrait -> landscape shortly after opening (once)
  useEffect(() => {
    if (open && orientation === 'portrait' && !autoSwitchedOrientation) {
      const id = setTimeout(() => {
        setOrientation('landscape');
        setAutoSwitchedOrientation(true);
      }, 150);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open, orientation, autoSwitchedOrientation]);

  // Cache weather widgets as images when dialog opens
  useEffect(() => {
    if (!open || mediaItems.length === 0) return;

    const cacheWeatherWidgets = async () => {
      const weatherItems = [];

      // Collect all weather items (direct and in split screen zones)
      mediaItems.forEach((item) => {
        const effectiveType =
          item.type === 'widget' || !item.type
            ? item.widget?.widgetCat?.toLowerCase() ?? ''
            : item.type;

        if (effectiveType === 'weather') {
          weatherItems.push(item);
        } else if (
          effectiveType === 'split_screen' ||
          effectiveType === 'split'
        ) {
          const zones = item.widget?.zones || [];
          zones.forEach((zone) => {
            const obj = zone.object;
            if (!obj) return;
            const zoneType = obj.type?.toLowerCase() || '';
            const zoneWidget = obj.widget;
            const zoneEffectiveType =
              zoneType === 'widget' || !zoneType
                ? zoneWidget?.widgetCat?.toLowerCase() ?? ''
                : zoneType;

            if (zoneEffectiveType === 'weather') {
              weatherItems.push({
                id: obj.id || zone.id,
                name: obj.name || zone.content_name || zone.name,
                type: zoneType,
                widget: zoneWidget,
                url: obj.url || zone.content_path || '',
                html: zoneWidget?.html || obj.html || '',
                duration: obj.duration || DEFAULT_DURATION,
                raw: obj,
              });
            }
          });
        }
      });

      if (weatherItems.length === 0) return;

      setIsCachingWeather(true);
      const cache = {};

      for (const weatherItem of weatherItems) {
        try {
          // Create hidden container
          const container = document.createElement('div');
          container.style.position = 'fixed';
          container.style.left = '-9999px';
          container.style.top = '0';
          container.style.width =
            orientation === 'portrait' ? '500px' : '1344px';
          container.style.height =
            orientation === 'portrait' ? '900px' : '756px';
          container.style.opacity = '1';
          container.style.pointerEvents = 'none';
          container.style.zIndex = '-1';
          document.body.appendChild(container);

          // Render CardWeather into container
          const root = await import('react-dom/client').then((m) =>
            m.createRoot(container)
          );
          root.render(
            <CardWeather
              item={{
                weather: weatherItem.widget?.weather,
                theme: weatherItem.widget?.theme,
              }}
              style={{ width: '100%', height: '100%' }}
              cssOverflow='auto'
              cssJustifyContent='center'
              cssFontSize={20}
              compact={false}
            />
          );

          // Poll for content readiness
          let attempts = 0;
          const maxAttempts = 20;
          while (attempts < maxAttempts) {
            await new Promise((r) => setTimeout(r, 500));
            const el = container.querySelector('.weather-widget');
            const ready = el?.querySelector('.weather-widget-header h1');
            if (ready) break;
            attempts++;
          }

          // Wait for all images and inline SVGs
          const weatherWidget =
            container.querySelector('.weather-widget') || container;
          const imgs = Array.from(weatherWidget.querySelectorAll('img'));
          const origin = window.location.origin;

          // Make all image URLs absolute
          imgs.forEach((img) => {
            try {
              const src = img.getAttribute('src') || '';
              if (src.startsWith('/')) {
                img.src = origin + src;
              }
              img.crossOrigin = 'anonymous';
            } catch {}
          });

          // Inline SVG images to avoid CORS issues
          await Promise.all(
            imgs.map(async (img) => {
              try {
                const src = img.getAttribute('src') || '';
                if (src && src.toLowerCase().endsWith('.svg')) {
                  const res = await fetch(src, { mode: 'cors' });
                  if (res.ok) {
                    const svgText = await res.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(
                      svgText,
                      'image/svg+xml'
                    );
                    const svgEl = doc.documentElement;
                    if (svgEl) {
                      const w =
                        img.getAttribute('width') || img.clientWidth || 32;
                      const h =
                        img.getAttribute('height') || img.clientHeight || 32;
                      svgEl.setAttribute('width', String(w));
                      svgEl.setAttribute('height', String(h));
                      img.replaceWith(svgEl);
                    }
                  }
                }
              } catch {}
            })
          );

          // Wait for all images to load
          await Promise.all(
            imgs.map(
              (img) =>
                new Promise((resolve) => {
                  if (img.complete && img.naturalWidth) return resolve();
                  img.addEventListener('load', resolve, { once: true });
                  img.addEventListener('error', resolve, { once: true });
                })
            )
          );

          // Convert to canvas then image
          const canvas = await html2canvas(weatherWidget, {
            useCORS: true,
            allowTaint: false,
            backgroundColor: null,
            logging: false,
            scale: 1.5,
            imageTimeout: 5000,
            removeContainer: true,
            width: orientation === 'portrait' ? 500 : 1344,
            height: orientation === 'portrait' ? 660 : 756,
          });

          const imageData = canvas.toDataURL('image/png');
          cache[weatherItem.id] = imageData;

          // Store in localStorage
          try {
            localStorage.setItem(`weather_cache_${weatherItem.id}`, imageData);
          } catch (e) {
            console.warn('Failed to store in localStorage:', e);
          }

          // Cleanup
          root.unmount();
          document.body.removeChild(container);
        } catch (error) {
          console.error(
            'Failed to cache weather widget:',
            weatherItem.id,
            error
          );
        }
      }

      setCachedWeatherImages(cache);
      setIsCachingWeather(false);
    };

    cacheWeatherWidgets();
  }, [open, mediaItems, orientation]);

  // Cleanup localStorage on dialog close
  useEffect(() => {
    if (!open) {
      // Clear all cached weather images from localStorage
      Object.keys(cachedWeatherImages).forEach((itemId) => {
        try {
          localStorage.removeItem(`weather_cache_${itemId}`);
        } catch (e) {
          console.warn('Failed to remove from localStorage:', e);
        }
      });
      setCachedWeatherImages({});
      setIsCachingWeather(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || mediaItems.length <= 1) return undefined;
    const timeout = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
    }, mediaItems[currentIndex]?.duration ?? DEFAULT_DURATION);

    return () => clearTimeout(timeout);
  }, [open, mediaItems, currentIndex]);

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  const goPrev = useCallback(() => {
    if (mediaItems.length <= 1) return;
    setCurrentIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length
    );
  }, [mediaItems.length]);

  const goNext = useCallback(() => {
    if (mediaItems.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  }, [mediaItems.length]);

  const currentItem = mediaItems[currentIndex];
  const showNavigation = !hideNav;
  const currentItemType = currentItem
    ? currentItem.type === 'widget' || !currentItem.type
      ? currentItem.widget?.widgetCat?.toLowerCase() ?? ''
      : currentItem.type
    : '';

  if (!open) return null;

  if(portable) {
    console.log(currentItem)
    return (
                <>
            {currentItem ? (
              <Box sx={{ width: '100%', aspectRatio: "1 / 0.56", position: 'relative', border: "2px solid #000000ff", backgroundColor: "#000" }}>
                {renderContent(currentItem, cachedWeatherImages)}
                {isCachingWeather && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  >
                    <CircularProgress sx={{ color: '#fff' }} />
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant='h6'>
                  {t('views.player.noContent', {
                    defaultValue: 'No content to preview',
                  })}
                </Typography>
              </Box>
            )}
        <div
          ref={hiddenCacheRef}
          style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
        />            
          </>
    )
  }
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      sx={{
        zIndex: 1000001,
      }}
      PaperProps={{
        sx: {
          // backgroundColor: theme.palette.grey[900],
          color: theme.palette.common.white,
        },
      }}
    >
      <DialogContent
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          p: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 1,
            zIndex: 1,
          }}
        >
          {showNavigation && (
            <Button
              size='small'
              variant='outlined'
              color='inherit'
              onClick={() =>
                setOrientation((o) =>
                  o === 'portrait' ? 'landscape' : 'portrait'
                )
              }
            >
              {orientation === 'portrait'
                ? t('views.player.switchLandscape', { defaultValue: 'Landscape' })
                : t('views.player.switchPortrait', { defaultValue: 'Portrait' })}
            </Button>
          )}
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.palette.common.white }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: 2,
          }}
        >
          {showNavigation ? (
            <IconButton
              size='small'
              onClick={goPrev}
              sx={{ color: theme.palette.common.white, fontSize: '70px' }}
            >
              <ArrowBackIosNewIcon fontSize='inherit' />
            </IconButton>
          ) : (
            <Box sx={{ width: '70px' }} />
          )}
          <TvFrame orientation={orientation}>
            {currentItem ? (
              <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                {renderContent(currentItem, cachedWeatherImages)}
                {isCachingWeather && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  >
                    <CircularProgress sx={{ color: '#fff' }} />
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant='h6'>
                  {t('views.player.noContent', {
                    defaultValue: 'No content to preview',
                  })}
                </Typography>
              </Box>
            )}
          </TvFrame>
          {showNavigation ? (
            <IconButton
              size='small'
              onClick={goNext}
              sx={{ color: theme.palette.common.white, fontSize: '70px' }}
            >
              <ArrowForwardIosIcon fontSize='inherit' />
            </IconButton>
          ) : (
            <Box sx={{ width: '70px' }} />
          )}
        </Box>

        {currentItem?.name ? (
          <Typography
            variant='body2'
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.85)',
              bgcolor: 'rgba(0,0,0,0.35)',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              backdropFilter: 'blur(2px)',
              width: 'fit-content',
            }}
          >
            {currentItem.name}
          </Typography>
        ) : null}
        {/* Hidden container for pre-rendering weather widgets */}
        <div
          ref={hiddenCacheRef}
          style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RedesignPlayer;
